import { useMemo, useRef, useState } from 'react';

import { chartColor, isDarkMode } from './palette';

export interface LinePoint {
    label: string;
    value: number;
}

interface LineChartProps {
    data: LinePoint[];
    height?: number;
    colorIndex?: number;
    formatValue?: (value: number) => string;
    /** Show the shaded area under the line */
    area?: boolean;
    className?: string;
}

/**
 * Single-series line/trend chart with a crosshair hover tooltip.
 * 2px line, subtle area fill, recessive grid. Pure SVG — no deps.
 */
const LineChart: React.FC<LineChartProps> = ({
    data,
    height = 180,
    colorIndex = 0,
    formatValue = (v) => String(v),
    area = true,
    className = '',
}) => {
    const dark = isDarkMode();
    const containerRef = useRef<HTMLDivElement>(null);
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);

    const width = 600; // viewBox width; scales responsively
    const padding = { top: 12, right: 12, bottom: 24, left: 40 };

    const { points, min, max, path, areaPath } = useMemo(() => {
        if (data.length === 0) {
            return { points: [] as { x: number; y: number }[], min: 0, max: 0, path: '', areaPath: '' };
        }
        const values = data.map((d) => d.value);
        const lo = Math.min(...values);
        const hi = Math.max(...values);
        const spread = hi - lo || 1;
        const innerW = width - padding.left - padding.right;
        const innerH = height - padding.top - padding.bottom;
        const pts = data.map((d, i) => ({
            x: padding.left + (data.length === 1 ? innerW / 2 : (i / (data.length - 1)) * innerW),
            y: padding.top + innerH - ((d.value - lo) / spread) * innerH,
        }));
        const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
        const baseY = padding.top + innerH;
        const fillPath = `${linePath} L${pts[pts.length - 1].x.toFixed(1)},${baseY} L${pts[0].x.toFixed(1)},${baseY} Z`;
        return { points: pts, min: lo, max: hi, path: linePath, areaPath: fillPath };
    }, [data, height]);

    if (data.length === 0) {
        return <div className={`text-sm text-slate-400 dark:text-slate-500 text-center py-6 ${className}`}>No data yet</div>;
    }

    const color = chartColor(colorIndex, dark);
    const gridColor = dark ? '#374151' : '#f3f4f6';
    const hovered = hoverIndex !== null ? data[hoverIndex] : null;
    const hoveredPoint = hoverIndex !== null ? points[hoverIndex] : null;

    const handleMove = (e: React.MouseEvent<SVGSVGElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * width;
        let nearest = 0;
        let best = Infinity;
        points.forEach((p, i) => {
            const dist = Math.abs(p.x - x);
            if (dist < best) {
                best = dist;
                nearest = i;
            }
        });
        setHoverIndex(nearest);
    };

    return (
        <div ref={containerRef} className={`relative ${className}`} role="img" aria-label="Line chart">
            <svg
                viewBox={`0 0 ${width} ${height}`}
                className="w-full"
                style={{ height }}
                onMouseMove={handleMove}
                onMouseLeave={() => setHoverIndex(null)}
            >
                {/* Grid */}
                {[0, 0.5, 1].map((t) => {
                    const y = padding.top + t * (height - padding.top - padding.bottom);
                    const value = max - t * (max - min);
                    return (
                        <g key={t}>
                            <line x1={padding.left} x2={width - padding.right} y1={y} y2={y} stroke={gridColor} strokeWidth={1} />
                            <text
                                x={padding.left - 6}
                                y={y + 3}
                                textAnchor="end"
                                fontSize={10}
                                fill={dark ? '#9ca3af' : '#6b7280'}
                            >
                                {formatValue(Math.round(value))}
                            </text>
                        </g>
                    );
                })}

                {/* Area + line */}
                {area && <path d={areaPath} fill={color} opacity={0.08} />}
                <path d={path} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />

                {/* Crosshair + marker */}
                {hoveredPoint && (
                    <g>
                        <line
                            x1={hoveredPoint.x}
                            x2={hoveredPoint.x}
                            y1={padding.top}
                            y2={height - padding.bottom}
                            stroke={dark ? '#6b7280' : '#d1d5db'}
                            strokeWidth={1}
                            strokeDasharray="3 3"
                        />
                        <circle cx={hoveredPoint.x} cy={hoveredPoint.y} r={5} fill={color} stroke={dark ? '#1f2937' : '#ffffff'} strokeWidth={2} />
                    </g>
                )}

                {/* First/last x labels */}
                <text x={padding.left} y={height - 6} fontSize={10} fill={dark ? '#9ca3af' : '#6b7280'}>
                    {data[0].label}
                </text>
                <text x={width - padding.right} y={height - 6} fontSize={10} textAnchor="end" fill={dark ? '#9ca3af' : '#6b7280'}>
                    {data[data.length - 1].label}
                </text>
            </svg>

            {/* Tooltip */}
            {hovered && hoveredPoint && (
                <div
                    className="absolute pointer-events-none z-10 px-2.5 py-1.5 rounded-lg text-xs shadow-lg border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 whitespace-nowrap"
                    style={{
                        left: `${(hoveredPoint.x / width) * 100}%`,
                        top: 0,
                        transform: `translateX(${hoveredPoint.x > width * 0.7 ? '-110%' : '10%'})`,
                    }}
                >
                    <div className="text-slate-500 dark:text-slate-400">{hovered.label}</div>
                    <div className="font-semibold text-slate-900 dark:text-white tabular-nums">{formatValue(hovered.value)}</div>
                </div>
            )}
        </div>
    );
};

export default LineChart;
